import { useQuery, useMutation, useQueryClient } from 'react-query';
import { supabase } from '../lib/supabase';
import { PaymentMethod } from '../lib/supabase';
import toast from 'react-hot-toast';

export const usePaymentMethods = (userId: string | undefined) => {
  const queryClient = useQueryClient();

  // Fetch payment methods
  const { data: paymentMethods, isLoading, error } = useQuery(
    ['paymentMethods', userId],
    async () => {
      if (!userId) return [];
      
      const { data, error } = await supabase
        .from('payment_methods')
        .select('*')
        .eq('user_id', userId)
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      return data || [];
    },
    {
      enabled: !!userId,
    }
  );

  // Add new payment method
  const addPaymentMethodMutation = useMutation(
    async (newPaymentMethod: Omit<PaymentMethod, 'id' | 'user_id' | 'created_at' | 'updated_at'>) => {
      if (!userId) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('payment_methods')
        .insert([{ ...newPaymentMethod, user_id: userId }])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['paymentMethods', userId]);
        toast.success('Payment method added successfully!');
      },
      onError: (error: any) => {
        toast.error(`Failed to add payment method: ${error.message}`);
      },
    }
  );

  // Update payment method
  const updatePaymentMethodMutation = useMutation(
    async ({ id, ...updateData }: Partial<PaymentMethod> & { id: string }) => {
      const { data, error } = await supabase
        .from('payment_methods')
        .update(updateData)
        .eq('id', id)
        .eq('user_id', userId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['paymentMethods', userId]);
        toast.success('Payment method updated successfully!');
      },
      onError: (error: any) => {
        toast.error(`Failed to update payment method: ${error.message}`);
      },
    }
  );

  // Delete payment method (soft delete by setting is_active to false)
  const deletePaymentMethodMutation = useMutation(
    async (id: string) => {
      const { error } = await supabase
        .from('payment_methods')
        .update({ is_active: false })
        .eq('id', id)
        .eq('user_id', userId);

      if (error) throw error;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['paymentMethods', userId]);
        toast.success('Payment method deleted successfully!');
      },
      onError: (error: any) => {
        toast.error(`Failed to delete payment method: ${error.message}`);
      },
    }
  );

  // Get payment method by name (for dropdowns)
  const getPaymentMethodByName = (name: string) => {
    return paymentMethods?.find(pm => pm.name === name);
  };

  // Get payment method names for dropdowns
  const getPaymentMethodNames = () => {
    return paymentMethods?.map(pm => pm.name) || [];
  };

  return {
    paymentMethods,
    isLoading,
    error,
    addPaymentMethod: addPaymentMethodMutation.mutateAsync,
    updatePaymentMethod: updatePaymentMethodMutation.mutateAsync,
    deletePaymentMethod: deletePaymentMethodMutation.mutateAsync,
    getPaymentMethodByName,
    getPaymentMethodNames,
    isAdding: addPaymentMethodMutation.isLoading,
    isUpdating: updatePaymentMethodMutation.isLoading,
    isDeleting: deletePaymentMethodMutation.isLoading,
  };
};
